import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	Paper,
	Snackbar,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { Globe, Save } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";

const DEFAULTS = {
	phone: "+91 97461 67477",
	email: "support@funmiyo.com",
	address: "Kerala, India",
	price_per_student: 550,
};

const WebSettings = () => {
	const { user } = useAuth();
	const [rowId, setRowId] = useState(null);
	const [form, setForm] = useState(DEFAULTS);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			setLoading(true);
			try {
				const { data, error } = await supabase
					.from("web_settings")
					.select("id, phone, email, address, price_per_student")
					.order("updated_at", { ascending: false })
					.limit(1)
					.maybeSingle();

				if (error) throw error;

				if (!cancelled && data) {
					setRowId(data.id);
					setForm({
						phone: data.phone || DEFAULTS.phone,
						email: data.email || DEFAULTS.email,
						address: data.address || DEFAULTS.address,
						price_per_student:
							data.price_per_student ?? DEFAULTS.price_per_student,
					});
				}
			} catch (err) {
				console.error("Failed to load web settings:", err);
				if (!cancelled) {
					setSnackbar({
						open: true,
						message: err.message || "Failed to load web settings",
						severity: "error",
					});
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, []);

	const updateField = (field) => (e) => {
		const value = e.target.value;
		setForm((prev) => ({
			...prev,
			[field]:
				field === "price_per_student"
					? value === ""
						? ""
						: Number(value)
					: value,
		}));
	};

	const isValidEmail = /\S+@\S+\.\S+/.test(form.email || "");
	const priceNum = Number(form.price_per_student);
	const isValidPrice =
		Number.isFinite(priceNum) && priceNum > 0 && Number.isInteger(priceNum);
	const canSave =
		(form.phone || "").trim() &&
		isValidEmail &&
		(form.address || "").trim() &&
		isValidPrice &&
		!saving;

	const handleSave = async () => {
		if (!canSave) return;
		setSaving(true);
		try {
			const payload = {
				phone: form.phone.trim(),
				email: form.email.trim(),
				address: form.address.trim(),
				price_per_student: priceNum,
				updated_at: new Date().toISOString(),
				updated_by: user?.id || null,
			};

			let error;
			if (rowId) {
				({ error } = await supabase
					.from("web_settings")
					.update(payload)
					.eq("id", rowId));
			} else {
				const { data, error: insertError } = await supabase
					.from("web_settings")
					.insert(payload)
					.select("id")
					.single();
				error = insertError;
				if (data?.id) setRowId(data.id);
			}

			if (error) throw error;

			setSnackbar({
				open: true,
				message: "Web settings saved. The public site will use these values.",
				severity: "success",
			});
		} catch (err) {
			console.error("Failed to save web settings:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to save web settings",
				severity: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Container maxWidth="md" sx={{ py: 4 }}>
			<Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
				<Globe size={28} />
				<Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
					Web Settings
				</Typography>
			</Stack>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
				Manage contact details and pricing shown on the public Funmiyo website
				(Talk to us, Start a conversation, and the investment section).
			</Typography>

			<Paper sx={{ p: 4, borderRadius: 2 }}>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<CircularProgress />
					</Box>
				) : (
					<Stack spacing={3}>
						<TextField
							label="Phone number"
							value={form.phone}
							onChange={updateField("phone")}
							helperText="Shown on the site and used for call / WhatsApp links."
							fullWidth
						/>
						<TextField
							label="Email"
							value={form.email}
							onChange={updateField("email")}
							error={form.email.length > 0 && !isValidEmail}
							helperText="Contact email shown on the website."
							fullWidth
						/>
						<TextField
							label="Address"
							value={form.address}
							onChange={updateField("address")}
							helperText="Location line shown in contact strips."
							fullWidth
						/>
						<TextField
							label="Price per student (₹)"
							type="number"
							value={form.price_per_student}
							onChange={updateField("price_per_student")}
							error={
								form.price_per_student !== "" &&
								form.price_per_student !== null &&
								!isValidPrice
							}
							helperText="Displayed as ₹{amount} per student per year."
							inputProps={{ min: 1, step: 1 }}
							fullWidth
						/>

						<Box>
							<Button
								variant="contained"
								color="primary"
								size="large"
								startIcon={
									saving ? <CircularProgress size={16} /> : <Save size={18} />
								}
								onClick={handleSave}
								disabled={!canSave}>
								{saving ? "Saving..." : "Save settings"}
							</Button>
						</Box>
					</Stack>
				)}
			</Paper>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default WebSettings;
